import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// Time parsing helper functions remain the same
const parseTimeToMinutes = (timeStr) => {
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let totalMinutes = hours * 60 + minutes;
  if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
  if (period === 'AM' && hours === 12) totalMinutes = minutes;
  return totalMinutes;
};

const formatMinutes = (minutes) => {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

const DynamicSchedule = ({ building, room }) => {
  const [schedule, setSchedule] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const response = await fetch('/data/cleaned_courses_f26.csv');
        
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {

            const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });
            
            const filteredData = results.data.filter(course => {
              let courseRoom = course.Room?.trim() || '';
              let compareRoom = room;
              let courseBuilding = course.Building;
              
              if (building === 'MLU') {
                // Remove any spaces and ensure proper format for comparison
                courseRoom = courseRoom.replace(/\s+/g, '');
                compareRoom = `MLU${room.replace(/\s+/g, '')}`;
                // Check for full building name
                if (courseBuilding === 'Martin Luther University College') {
                  courseBuilding = 'MLU';
                }
              }
              
              return (
                courseBuilding === building && 
                courseRoom === compareRoom &&
                course.Days.includes(currentDay)
              );
            });

            

            const todayClasses = Object.values(
              filteredData
                .reduce((acc, course) => {
                  const timeKey = course['Time Range'];
                  if (!acc[timeKey] || course.id < acc[timeKey].id) {
                    acc[timeKey] = course;
                  }
                  return acc;
                }, {})
            )
              .map(course => {
                const [startTime, endTime] = course['Time Range'].split(' - ');
                return {
                  ...course,
                  startMinutes: parseTimeToMinutes(startTime),
                  endMinutes: parseTimeToMinutes(endTime)
                };
              })
              .sort((a, b) => a.startMinutes - b.startMinutes);

            const fullSchedule = [];
            let lastEndTime = 8 * 60; // Start at 8 AM
            const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
            
            todayClasses.forEach(course => {
              // Only add free time slots if they're 30 minutes or longer
              if (course.startMinutes > lastEndTime && 
                  (course.startMinutes - lastEndTime) >= 30) {
                fullSchedule.push({
                  type: 'free',
                  startMinutes: lastEndTime,
                  endMinutes: course.startMinutes,
                  isInProgress: currentMinutes >= lastEndTime && currentMinutes < course.startMinutes
                });
              }
              
              fullSchedule.push({
                type: 'class',
                ...course,
                isInProgress: currentMinutes >= course.startMinutes && currentMinutes < course.endMinutes
              });
              
              lastEndTime = course.endMinutes;
            });

            // Only add final free time slot if it's 30 minutes or longer
            if (lastEndTime < 22 * 60 && 
                (22 * 60 - lastEndTime) >= 30) {
              fullSchedule.push({
                type: 'free',
                startMinutes: lastEndTime,
                endMinutes: 22 * 60,
                isInProgress: currentMinutes >= lastEndTime && currentMinutes < 22 * 60
              });
            }

            

            setSchedule(fullSchedule);
            setCurrentEvent(fullSchedule.find(slot => slot.isInProgress));
          },
          error: (error) => {
          }
        });
      } catch (error) {
      }
    };

    fetchSchedule();
  }, [building, room, currentTime]);

  useEffect(() => {
  }, [schedule, currentEvent, isExpanded]);

  const renderTimeSlot = (slot, index) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      key={index}
      className={`relative p-5 rounded-2xl transition-all duration-300
        ${slot.type === 'free' 
          ? 'bg-gradient-to-r from-slate-800/40 to-slate-800/20' 
          : 'bg-gradient-to-r from-slate-800/40 to-slate-800/20 border-l-4 border-l-blue-400'
        } ${
          slot.isInProgress ? 'ring-1 ring-blue-400/50 shadow-[0_0_15px_rgba(59,130,246,0.1)]' : ''
        }
        hover:shadow-lg hover:shadow-slate-900/20 backdrop-blur-xl
        border border-white/5`}
    >
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex items-center justify-between sm:justify-start gap-4">
          <span className="text-white/90 font-medium whitespace-nowrap text-base">
            {formatMinutes(slot.startMinutes)} - {formatMinutes(slot.endMinutes)}
          </span>
          {slot.type === 'class' && (
            <span className={`px-4 py-1.5 rounded-full text-sm font-medium
              ${slot.isInProgress 
                ? 'bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/30' 
                : 'bg-slate-700/40 text-white/90'
              } backdrop-blur-md`}>
              {slot.isInProgress ? 'In Progress' : 'Scheduled'}
            </span>
          )}
        </div>

        <div className="flex items-center min-w-0 text-right">
          {slot.type === 'class' ? (
            <div>
              <p className="text-white/90 font-semibold tracking-wide">
                {slot.Course || 'Lecture'}
              </p>
            </div>
          ) : (
            <p className="text-emerald-400 font-semibold tracking-wide">
              Available
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-4">
      {schedule.length === 0 ? (
        <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-800/40 to-slate-800/20 
                      border border-white/5 backdrop-blur-xl">
          <p className="text-emerald-400 font-semibold tracking-wide">Available all day</p>
        </div>
      ) : (
        <>
          {/* Current/Next Event Preview */}
          {currentEvent && renderTimeSlot(currentEvent, 'current')}
          
          {/* Expandable Schedule */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4 overflow-hidden"
              >
                {schedule
                  .filter(slot => slot !== currentEvent)
                  .map((slot, index) => renderTimeSlot(slot, index))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full p-3 rounded-2xl bg-gradient-to-r from-slate-800/40 to-slate-800/20 
                     border border-white/5 backdrop-blur-xl text-white/70 
                     hover:text-white transition-colors flex items-center justify-center gap-2"
          >
            <span>{isExpanded ? 'Show Less' : 'Show Full Schedule'}</span>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </button>
        </>
      )}
    </div>
  );
};

export default DynamicSchedule;